import { Component, ElementRef, NgZone } from '@angular/core';
import { ActivatedRoute, Params, Router} from '@angular/router';
import { circle, icon, latLng, Layer, Marker, marker} from 'leaflet';
import { ConfigService } from '../../services/config/config.service';
import { DateService } from '../../services/date/date.service';
import { GpsLogDateGroup } from '../../services/gps-logs/gps-log-date-group.model';
import { SearchService } from '../../services/search/search.service';
import { GpsLogItem } from '../../services/gps-logs/gps-log-item.model';
import { Business } from '../../services/trucks/truck.model';

import { BingLayerService } from '../../services/bing-layer/bing-layer.service';


@Component({
  selector: 'app-search-result-tabs',
  templateUrl: './search-result-tabs.component.html',
  styleUrls: ['./search-result-tabs.component.css']
})
export class SearchResultTabsComponent {

  display: string;

  layers: Layer[];

  options = {
    zoom: 18,
    center: latLng(-27.524494, 153.254786)
  };

  private _route: ActivatedRoute;
  private _selectedDateGroup: GpsLogDateGroup;

  constructor(private activatedRoute: ActivatedRoute,
              public searchService: SearchService,
              private dateService: DateService,
              private router: Router,
              private config: ConfigService,
              private bingLayerService: BingLayerService,
              private elementRef: ElementRef,
              private ngZone: NgZone) {

      this._route = this.activatedRoute;

      this.activatedRoute.queryParams.subscribe(params => {
        this.display = params['display'];
        if (!this.display || this.display === '') {
          this.display = 'list';
        }

        if (this.display === 'map') {

          this.createMapLayer(searchService, this.config);

          const dateGroupParam = params['selectedDateGroup'];
          if (dateGroupParam && searchService.gpsLogDateGroups) {
            const dateGroup = new Date(dateGroupParam);
            if (dateGroup instanceof Date) {
              this._selectedDateGroup = searchService.gpsLogDateGroups
                .find(dg => dg.date.getDate() === dateGroup.getDate());
              if (this._selectedDateGroup) {
                this._selectedDateGroup.devices.forEach(dvc => {
                  dvc.items.forEach(item => {
                    this.addTruckMarker(item, params);
                  });
                });
              }
            }
          } else {
            if (searchService.gpsLogItems) {
              searchService.gpsLogItems.forEach(item => {
                this.addTruckMarker(item, params);
              });
            }
          }
        }
      });
  }

  selectDateGroup(group: GpsLogDateGroup): void {
    this._selectedDateGroup = group;

    this.router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        'selectedDateGroup': group.date
      },
      queryParamsHandling: 'merge'
    });
  }

  get selectedDateGroup(): GpsLogDateGroup {
    return this._selectedDateGroup;
  }

  private addTruckMarker(item: GpsLogItem, params: Params): void {
    if (item.truckNumber) {
      const isSelected = this.isSelectedGpsLog(item, params);
      const mkr = this.createMarker(item, isSelected);
      this.layers.push(mkr);
    }
  }

  private createMapLayer(searchService: SearchService, config: ConfigService): void {
    if (this.display === 'map') {
      this.layers = [];
      // center the map on the search results
      this.options.center = latLng(searchService.latitude, searchService.longitude);
      this.layers.push( this.bingLayerService.bingLayer(config.config.bingMapsApiKey, { maxZoom: 19, attribution: 'Bing' }));
      this.layers.push(circle([searchService.latitude, searchService.longitude], { radius: 10 }));
    }
  }

  private createMarker(log: GpsLogItem, isSelected: boolean): Marker<any> {

    let iconUrl = 'assets/marker.svg';

    if (isSelected) {
      iconUrl = 'assets/marker-selected.svg';
    }

    const mkr = marker([log.latitude, log.longitude], {
      icon: icon({
        iconSize: [ 25, 41 ],
        iconAnchor: [ 13, 41 ],
        iconUrl: iconUrl,
        shadowUrl: 'assets/marker-shadow.png'
      })});

    if (isSelected) {
      mkr.setZIndexOffset(100);
    }

    let popupLink = '<strong>' + this.dateService.momentToClosestMinute(log.timestamp).toISOString(true) + '</strong><br/>';
    popupLink += `<a class="createvideorequest" data-cy="create-video-request">Create video request</a> for ` + log.truckNumber;

    mkr.bindPopup(popupLink)
       .on("popupopen", () => {
        this.elementRef.nativeElement
          .querySelector(".createvideorequest")
          .addEventListener("click", e => {
            this.createVideoRequestFromMap(log);
          });
      });
    return mkr;
  }

  private createVideoRequestFromMap(log: GpsLogItem): void {
    const address = this.router.routerState.snapshot.root.queryParams['address'];
    const location = address ? address : this.searchService.latitude + ',' + this.searchService.longitude;
    this.ngZone.run(() => {
      this.router.navigate(['create-video-request', log.truckNumber], {
          queryParams: {
            computerType: log.computerType,
            deviceId: log.deviceId,
            ipAddress: log.ipAddress,
            location: location,
            site: this.searchService.site.siteNumber,
            softwareVersion: log.softwareVersion,
            timestamp: this.dateService.momentToClosestMinute(log.timestamp).format(),
            business: Business[log.business],
          }
      });
    });
  }

  private isSelectedGpsLog(log: GpsLogItem, params: Params): boolean {
    return  log.truckNumber === params['truckNumber'] &&
            log.computerType === params['computerType'] &&
            log.deviceId === params['deviceId'] &&
            log.ipAddress === params['ipAddress'] &&
            log.softwareVersion.toString() === params['softwareVersion'] &&
            this.dateService.momentToClosestMinute(log.timestamp).format() === params['timestamp'];
  }
}
